import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LinearProgress } from "@mui/material";
import {
    ItemSelectorField,
    type ItemSelectorTableColumn,
} from "@/components/ItemSelectorDialog";
import { fetchItemsAPI } from "./api/itemsApi";
import { fetchComponentsFromAPI } from "./api/componentsApi";
import { resolveCollectionIdentifier } from "./utils/collectionUtils";
import { normalizeOrgSegment } from "./utils/collectionPaths";
import { isOrganizationScopingDisabled } from "./utils/organizationScoping";
import { Label } from "@/components/ui/label";
import { ActionTypes } from "@/actions";
import DebugFieldButton from "@/components/DebugFieldButton";
import { getSystemRelationSource } from "./utils/systemRelationSources";

export enum DefaultSelectOptions {
    All = "all",
    None = "none"
}

function formatRelationFieldFetchError(err: unknown): string {
    const statusSuffix = (() => {
        if (!err || typeof err !== "object") return "";
        const st = (err as { status?: number }).status;
        return typeof st === "number" ? ` (HTTP ${st})` : "";
    })();
    if (err instanceof Error) {
        const raw = err.message.trim();
        if (!raw) return `Request failed.${statusSuffix}`;
        try {
            const parsed = JSON.parse(raw) as { message?: string; error?: string };
            if (parsed && typeof parsed === "object") {
                if (typeof parsed.message === "string" && parsed.message.trim()) {
                    return `${parsed.message.trim()}${statusSuffix}`;
                }
                if (typeof parsed.error === "string" && parsed.error.trim()) {
                    return `${parsed.error.trim()}${statusSuffix}`;
                }
            }
        } catch {
            /* plain text */
        }
        return `${raw}${statusSuffix}`;
    }
    const s = String(err ?? "").trim();
    return (s || "Unknown error.") + statusSuffix;
}

/** Single value or relation object → uid string for Select / comparisons */
function relationValueToUidString(value: unknown): string {
    if (value == null || value === "") return "";
    if (typeof value === "object" && value !== null) {
        const o = value as { uid?: unknown; id?: unknown };
        const u = o.uid ?? o.id;
        if (u != null && String(u).trim() !== "") return String(u).trim();
        return "";
    }
    return String(value).trim();
}

function getNestedField(obj: any, path: string): any {
    if (!obj || typeof obj !== "object" || !path) return undefined;
    if (!path.includes(".")) return obj[path];
    const keys = path.split(".");
    let v = obj;
    for (const k of keys) {
        if (v && typeof v === "object" && k in v) {
            v = v[k];
        } else {
            return undefined;
        }
    }
    return v;
}

function relationRowRichness(row: any): number {
    if (!row || typeof row !== "object") return 0;
    return Object.keys(row).filter((k) => row[k] != null && row[k] !== "").length;
}

/** One row per uid; prefer richer API rows over thin stubs when duplicates appear. */
function dedupeRelationPickerRows(rows: any[]): any[] {
    const bestByUid = new Map<string, any>();
    for (const row of rows) {
        if (row?.uid == null) continue;
        const k = String(row.uid);
        const prev = bestByUid.get(k);
        if (!prev || relationRowRichness(row) > relationRowRichness(prev)) {
            bestByUid.set(k, row);
        }
    }
    const seen = new Set<string>();
    const out: any[] = [];
    for (const row of rows) {
        const k = row?.uid != null ? String(row.uid) : "";
        if (!k || seen.has(k)) continue;
        seen.add(k);
        const best = bestByUid.get(k);
        if (best) out.push(best);
    }
    return out;
}

function relationPickerRowLabelKey(data: any, dropdownFields: any[]): string {
    const itemValue = data?.uid != null ? String(data.uid) : "";
    const displayText =
        dropdownFields.length > 0
            ? dropdownFields
                .map((field: any) => formatDateOnlyLabel(data?.[field.name]))
                .filter(Boolean)
                .join(" ") || itemValue
            : formatDateOnlyLabel(data?.name || data?.label || itemValue);
    return (displayText || itemValue).trim().toLowerCase();
}

/** Prefer canonical API rows when the same country/name appears twice with different uids. */
function pickBetterRelationPickerDuplicate(a: any, b: any): any {
    const score = (r: any) => {
        const u = r?.uid != null ? String(r.uid) : "";
        let s = relationRowRichness(r);
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(u)) s += 1000;
        else if (/^[a-f0-9]{24}$/i.test(u)) s += 1000;
        return s;
    };
    return score(a) >= score(b) ? a : b;
}

/**
 * One visible option per display label (e.g. duplicate "Netherlands" rows in data).
 * Keeps sort order. If the edited item points at a non-preferred uid, that row wins for its label so the Select still matches.
 */
function buildSingleSelectMenuRows(
    sortedAllData: any[],
    dropdownFields: any[],
    currentValue: string
): any[] {
    const labelKeyFor = (data: any) => relationPickerRowLabelKey(data, dropdownFields);

    const byLabel = new Map<string, any>();
    for (const data of sortedAllData) {
        const k = labelKeyFor(data);
        if (!k) continue;
        const cur = byLabel.get(k);
        if (!cur) {
            byLabel.set(k, data);
        } else {
            byLabel.set(k, pickBetterRelationPickerDuplicate(cur, data));
        }
    }

    const emitted = new Set<string>();
    const menuRows: any[] = [];
    for (const data of sortedAllData) {
        const k = labelKeyFor(data);
        if (!k) {
            menuRows.push(data);
            continue;
        }
        const winner = byLabel.get(k);
        if (data !== winner) continue;
        if (emitted.has(k)) continue;
        emitted.add(k);
        menuRows.push(data);
    }

    const cv = String(currentValue || "").trim();
    if (cv && !menuRows.some((r) => r?.uid != null && String(r.uid) === cv)) {
        const fallback = sortedAllData.find((r) => r?.uid != null && String(r.uid) === cv);
        if (fallback) {
            const fk = labelKeyFor(fallback);
            if (fk) {
                const idx = menuRows.findIndex((r) => labelKeyFor(r) === fk);
                if (idx >= 0) {
                    menuRows[idx] = fallback;
                } else {
                    menuRows.unshift(fallback);
                }
            } else {
                menuRows.unshift(fallback);
            }
        }
    }

    return menuRows;
}

/** Stubs for current value(s) missing from the relate list, then API rows; deduped so edits do not leave orphan stubs. */
function mergeRelationPickerStubs(
    items: any[],
    current: any,
    relationFieldKey: string,
    multiple: boolean | undefined
): any[] {
    const list = Array.isArray(items) ? items : [];
    if (!current || typeof current !== "object" || !relationFieldKey) {
        return dedupeRelationPickerRows(list);
    }
    const existing = new Set(
        list.map((i) => (i?.uid != null ? String(i.uid) : "")).filter(Boolean)
    );
    const stubs: any[] = [];

    if (multiple) {
        const raw = getNestedField(current, relationFieldKey);
        const arr = Array.isArray(raw) ? raw : raw != null && raw !== "" ? [raw] : [];
        const labelsRaw = current[`${relationFieldKey}__labels`];
        const labelArr = Array.isArray(labelsRaw) ? labelsRaw : null;
        arr.forEach((entry: any, idx: number) => {
            const uid = relationValueToUidString(entry);
            if (!uid || existing.has(uid)) return;
            existing.add(uid);
            const lab =
                labelArr && labelArr[idx] != null && String(labelArr[idx]).trim() !== ""
                    ? String(labelArr[idx]).trim()
                    : uid;
            stubs.push({ uid, name: lab, label: lab });
        });
    } else {
        const raw = getNestedField(current, relationFieldKey);
        const uid = relationValueToUidString(raw);
        if (uid && !existing.has(uid)) {
            const labelKey = `${relationFieldKey}__label`;
            const lr = current[labelKey];
            const lab = typeof lr === "string" && lr.trim() !== "" ? lr.trim() : uid;
            stubs.push({ uid, name: lab, label: lab });
        }
    }

    const combined = stubs.length > 0 ? [...stubs, ...list] : list;
    return dedupeRelationPickerRows(combined);
}

function formatDateOnlyLabel(value: unknown): string {
    if (value == null) return "";
    const raw = String(value).trim();
    if (!raw) return "";
    const dateOnlyMatch = raw.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (dateOnlyMatch) return dateOnlyMatch[1];
    const isoUtcMatch = raw.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/);
    if (!isoUtcMatch) return raw;
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return isoUtcMatch[1];
    return parsed.toDateString();
}

function relationRowDisplayLabel(data: any, dropdownFields: any[]): string {
    const itemValue = data?.uid != null ? String(data.uid) : "";
    if (dropdownFields.length > 0) {
        const text = dropdownFields
            .map((field: any) => formatDateOnlyLabel(data?.[field.name]))
            .filter(Boolean)
            .join(" ");
        if (text) return text;
    }
    return (
        formatDateOnlyLabel(data?.name || data?.label || data?.title || itemValue) ||
        itemValue
    );
}

function relationTableColumns(dropdownFields: any[]): ItemSelectorTableColumn[] {
    const cols: ItemSelectorTableColumn[] = dropdownFields.map((field: any) => ({
        key: field.name,
        header: field.label || field.name,
    }));
    cols.push({ key: "uid", header: "UID" });
    return cols;
}

function relationRowToSelectorItem(data: any, dropdownFields: any[]) {
    const value = data?.uid != null ? String(data.uid) : "";
    const label = relationRowDisplayLabel(data, dropdownFields) || value || "—";
    const cells: Record<string, string> = { uid: value };

    if (dropdownFields.length > 0) {
        for (const field of dropdownFields) {
            cells[field.name] = formatDateOnlyLabel(data?.[field.name]) || "";
        }
    } else {
        const name = formatDateOnlyLabel(data?.name || data?.label || data?.title);
        if (name) cells.name = name;
    }

    return {
        value,
        label,
        cells,
        data,
    };
}

function sortRelationRows(allData: any[], dropdownFields: any[]): any[] {
    return [...allData].sort((a: any, b: any) => {
        const aLabel = relationRowDisplayLabel(a, dropdownFields);
        const bLabel = relationRowDisplayLabel(b, dropdownFields);
        return String(aLabel).localeCompare(String(bLabel), undefined, {
            sensitivity: "base",
            numeric: true,
        });
    });
}

// Label wrapper component for RelationField - matches the pattern used in the codebase
const LabelTyped = ({ text, children, debugProps }: { text: string, children: React.ReactNode, debugProps?: any }) => {
    return (
        <div className="space-y-2">
            <div className="flex items-center space-x-2">
                <Label className="text-sm font-medium text-gray-700">{text}</Label>
                {debugProps && <DebugFieldButton {...debugProps} />}
            </div>
            {children}
        </div>
    );
}

export type RelationFieldProps = {
    table: string;
    label: string;
    fields: string;
    relationFieldKey: string;
    canEdit: boolean;
    onChange: any;
    /** Caller’s group memberships; reserved for relation pickers that scope options by group (e.g. users). */
    allowedGroups?: string[];
    multiple?: boolean;
    readOnlyUids?: any;
    defaultSelect?: DefaultSelectOptions;
    hideLabel?: boolean;
    enableSearch?: boolean;
    systemSource?: string;
    /** Parent / route collection org — required for org-scoped related components to resolve on `aclAction=relate` list. */
    listOrganization?: string | null;
};

const RelationField = ({
    table,
    label,
    fields,
    relationFieldKey,
    canEdit,
    onChange,
    allowedGroups,
    multiple,
    readOnlyUids: readOnlyUids,
    defaultSelect: _defaultSelect,
    hideLabel: hideLabel,
    enableSearch: _enableSearch,
    systemSource,
    listOrganization,
}: RelationFieldProps) => {

    const dispatch = useDispatch()
    const [allData, setAllData] = useState<any>([])
    const [isLoading, setIsLoading] = useState(false)
    const current = useSelector((state: any) => state.item.current)
    const currentForPickerRef = useRef(current)
    useEffect(() => {
        currentForPickerRef.current = current
    }, [current])
    const [currentState, setCurrentState] = useState(current || {})
    const [filteredStatic, setFilteredStatic] = useState<any>([])
    const user = useSelector((state: any) => state.user)
    const tableMetaData = useSelector((state: any) => state.table.meta)
    // Memoize admin check to prevent unnecessary re-renders
    const isAdmin = useMemo(() => {
        const adminValue = user?.admin || user?.info?.admin;
        console.log('RelationField: Admin check', {
            userAdmin: user?.admin,
            userInfoAdmin: user?.info?.admin,
            isAdmin: adminValue,
            user: user ? Object.keys(user) : null,
            userInfo: user?.info ? Object.keys(user.info) : null
        });
        return adminValue;
    }, [user?.admin, user?.info?.admin])
    const [selectedMeta, setSelectedMeta] = useState<any>(undefined)
    const [selectedUids, setSelectedUids] = useState<any>([])
    const [dropdownFields, setDropdownFields] = useState<any>([])
    const [hasError, setHasError] = useState(false)
    const [loadErrorMessage, setLoadErrorMessage] = useState("")
    const isFetchingRef = useRef(false)
    const lastSelectedMetaRef = useRef<{ uid?: string, slug?: string } | null>(null)
    const lastFetchedMetaRef = useRef<{ uid?: string, slug?: string } | null>(null)
    const lastFetchedRelateItemsRef = useRef<any[]>([])
    const lastSystemRelationItemsRef = useRef<any[]>([])

    useEffect(() => {
        lastFetchedRelateItemsRef.current = [];
        lastSystemRelationItemsRef.current = [];
    }, [table, systemSource])

    useEffect(() => {
        if (systemSource) {
            const systemMeta = getSystemRelationSource(systemSource);
            if (systemMeta) {
                setSelectedMeta({
                    uid: systemMeta.key,
                    slug: systemMeta.key,
                    fields: systemMeta.fields
                });
                setHasError(false);
                setLoadErrorMessage("");
            } else {
                setSelectedMeta(undefined);
            }
            return;
        }
        // Sanitize table value (must be a slug)
        const sanitizedTable = typeof table === 'string' ? table.replace(/[!]+$/, '').trim() : table;

        // Only match by slug - no fallbacks
        const _selectedMeta = tableMetaData?.find((meta: any) => {
            // Sanitize component slug for comparison
            const compSlugSanitized = typeof meta.slug === 'string' ? meta.slug.replace(/[!]+$/, '').trim() : meta.slug;
            // Match by slug only
            return (
                compSlugSanitized === sanitizedTable ||
                meta.slug === sanitizedTable ||
                compSlugSanitized === table ||
                meta.slug === table
            );
        });

        // If slug not found in tableMetaData, don't dispatch error yet
        // The fetchData function will try to fetch from API and handle errors there
        // This prevents false errors when component metadata hasn't loaded yet
        if (!_selectedMeta && table) {
            console.log('⚠️ RelationField: Component slug not found in tableMetaData, will try API fetch', {
                table: sanitizedTable,
                tableMetaDataCount: tableMetaData?.length || 0
            });
            // Keep API-resolved stub meta (catalog often omits collections user can relate to but not list in /collections/components).
            setSelectedMeta((prev: any) => {
                const pslug =
                    typeof prev?.slug === 'string' ? prev.slug.replace(/[!]+$/, '').trim() : '';
                if (pslug === sanitizedTable) return prev;
                return undefined;
            });
        } else if (_selectedMeta) {
            // Component found in tableMetaData, clear any previous errors
            setHasError(false);
            setLoadErrorMessage("");
        }

        // Check if the selected meta actually changed by comparing uid and slug
        const currentMetaKey = _selectedMeta ? { uid: _selectedMeta.uid, slug: _selectedMeta.slug } : null;
        const lastMetaKey = lastSelectedMetaRef.current;

        // Determine if meta actually changed
        // If lastSelectedMetaRef is null, this is the first run, so we should update
        const isFirstRun = lastSelectedMetaRef.current === null;
        const metaKeyChanged = isFirstRun ||
            (!currentMetaKey && lastMetaKey) || // Was found, now not found
            (currentMetaKey && !lastMetaKey) || // Was not found, now found
            (currentMetaKey && lastMetaKey && (
                currentMetaKey.uid !== lastMetaKey.uid ||
                currentMetaKey.slug !== lastMetaKey.slug
            )); // Found both, but different

        if (!metaKeyChanged) {
            // Selected meta hasn't actually changed, skip update to prevent unnecessary re-renders
            console.log('🔄 RelationField: Skipping selectedMeta update - meta unchanged', {
                table,
                currentUid: currentMetaKey?.uid,
                currentSlug: currentMetaKey?.slug,
                lastUid: lastMetaKey?.uid,
                lastSlug: lastMetaKey?.slug,
                isFirstRun
            });
            return;
        }

        console.log('🔄 RelationField: Updating selectedMeta', {
            table,
            metaChanged: metaKeyChanged,
            currentUid: currentMetaKey?.uid,
            currentSlug: currentMetaKey?.slug,
            hadMeta: !!lastMetaKey
        });

        lastSelectedMetaRef.current = currentMetaKey;

        if (typeof (_selectedMeta) !== 'undefined') {
            // Only update if the meta actually changed (compare by uid or slug to avoid reference equality issues)
            setSelectedMeta((prev: any) => {
                if (prev?.uid === _selectedMeta?.uid && prev?.slug === _selectedMeta?.slug) {
                    return prev; // Return previous value to prevent unnecessary re-renders
                }
                return _selectedMeta;
            })
        } else {
            setSelectedMeta(undefined)
        }

    }, [table, tableMetaData, systemSource])


    useEffect(() => {
        const fetchData = async () => {
            // Prevent duplicate fetches
            if (isFetchingRef.current) {
                console.log('⏭️ RelationField: Skipping fetch - already fetching', { table });
                return;
            }

            if (systemSource) {
                const systemMeta = getSystemRelationSource(systemSource);
                if (!systemMeta) {
                    setHasError(true);
                    setLoadErrorMessage(
                        systemSource
                            ? `Unknown system relation source "${String(systemSource)}".`
                            : "Unknown system relation source."
                    );
                    lastSystemRelationItemsRef.current = [];
                    setAllData([]);
                    return;
                }
                isFetchingRef.current = true;
                setIsLoading(true);
                setHasError(false);
                setLoadErrorMessage("");
                try {
                    const items = await systemMeta.fetchItems();
                    const raw = Array.isArray(items) ? items : [];
                    lastSystemRelationItemsRef.current = raw.slice();
                    setAllData(
                        mergeRelationPickerStubs(
                            lastSystemRelationItemsRef.current,
                            currentForPickerRef.current,
                            relationFieldKey,
                            multiple
                        )
                    );
                    setHasError(false);
                    setLoadErrorMessage("");
                } catch (error) {
                    console.error('Failed to fetch system relation data:', error);
                    setHasError(true);
                    setLoadErrorMessage(formatRelationFieldFetchError(error));
                    lastSystemRelationItemsRef.current = [];
                    setAllData([]);
                } finally {
                    isFetchingRef.current = false;
                    setIsLoading(false);
                }
                return;
            }

            if (!table) {
                console.log('⏭️ Skipping fetch - no table', { table });
                return;
            }

            // Check if selectedMeta actually changed in a meaningful way
            const currentMetaKey = selectedMeta ? { uid: selectedMeta.uid, slug: selectedMeta.slug } : null;
            const lastFetchedMetaKey = lastFetchedMetaRef.current;
            const metaKeyChanged =
                (!currentMetaKey && lastFetchedMetaKey) || // Was found, now not found
                (currentMetaKey && !lastFetchedMetaKey) || // Was not found, now found
                (currentMetaKey && lastFetchedMetaKey && (
                    currentMetaKey.uid !== lastFetchedMetaKey.uid ||
                    currentMetaKey.slug !== lastFetchedMetaKey.slug
                )); // Found both, but different

            // If meta hasn't changed and we've already fetched, skip
            if (!metaKeyChanged && lastFetchedMetaKey !== null && selectedMeta) {
                console.log('⏭️ RelationField: Skipping fetch - selectedMeta unchanged', {
                    table,
                    currentUid: currentMetaKey?.uid,
                    currentSlug: currentMetaKey?.slug,
                    lastUid: lastFetchedMetaKey?.uid,
                    lastSlug: lastFetchedMetaKey?.slug
                });
                return;
            }

            console.log('🔄 RelationField: Starting fetchData', {
                table,
                hasSelectedMeta: !!selectedMeta,
                selectedMetaSlug: selectedMeta?.slug,
                metaKeyChanged
            });

            isFetchingRef.current = true;
            setIsLoading(true);
            setHasError(false);
            setLoadErrorMessage("");
            let resolvedViaCatalogOnly = false;
            let relatedComponent: any = null;
            try {
                // Determine the correct collection name based on metadata
                let collectionName: string | undefined = table;
                const sanitizedTableForPath =
                    typeof table === 'string' ? table.replace(/[!]+$/, '').trim() : String(table);

                // First, try to use selectedMeta if available
                if (selectedMeta && typeof selectedMeta === 'object' && selectedMeta !== null) {
                    relatedComponent = selectedMeta;
                    // Use resolveCollectionIdentifier to properly resolve collection name
                    collectionName = resolveCollectionIdentifier(selectedMeta, table);
                    console.log('✅ RelationField: Using selectedMeta for collection', {
                        collectionName,
                        dataSource: selectedMeta.dataSource,
                        slug: selectedMeta.slug,
                        mongoCollection: selectedMeta.mongoCollection
                    });
                } else {
                    // If selectedMeta is not available, fetch components to find the matching one by slug only
                    console.log('⚠️ RelationField: selectedMeta not available, fetching components from API', { table });
                    const components = await fetchComponentsFromAPI();

                    // Only match by slug - no fallbacks
                    relatedComponent = components.find((comp: any) => {
                        // Sanitize component slug for comparison
                        const compSlugSanitized = typeof comp.slug === 'string' ? comp.slug.replace(/[!]+$/, '').trim() : comp.slug;
                        // Match by slug only
                        return (
                            compSlugSanitized === sanitizedTableForPath ||
                            comp.slug === sanitizedTableForPath ||
                            compSlugSanitized === table ||
                            comp.slug === table
                        );
                    });

                    if (relatedComponent) {
                        // Use resolveCollectionIdentifier to properly resolve collection name
                        collectionName = resolveCollectionIdentifier(relatedComponent, table);
                        console.log('✅ RelationField: Found component and resolved collection', {
                            componentName: relatedComponent.name,
                            componentSlug: relatedComponent.slug,
                            collectionName,
                            dataSource: relatedComponent.dataSource
                        });
                        // Update selectedMeta so it's available for dropdown fields logic
                        setSelectedMeta(relatedComponent);
                        // Clear local relation error state now that component resolution succeeded.
                        setHasError(false);
                        setLoadErrorMessage("");
                    } else {
                        // Catalog omits this slug (e.g. user has relate/view on related collection but no listItems on /collections/components).
                        relatedComponent = {
                            slug: sanitizedTableForPath,
                            fields: [],
                            ...(!isOrganizationScopingDisabled() &&
                                listOrganization != null &&
                                String(listOrganization).trim() !== '' && {
                                organization: String(listOrganization).trim()
                            })
                        };
                        collectionName = sanitizedTableForPath;
                        resolvedViaCatalogOnly = true;
                        console.log('⚠️ RelationField: slug not in components catalog; loading options via items API', {
                            slug: sanitizedTableForPath
                        });
                    }
                }

                if (!collectionName || collectionName === 'default_collection') {
                    const errorMsg = `Collection name could not be determined for slug "${table}". Configure collection or mongoCollection on the related component.`;
                    console.error('❌ RelationField:', errorMsg, {
                        selectedMeta: selectedMeta ? {
                            slug: selectedMeta.slug,
                            collection: selectedMeta.collection,
                            mongoCollection: selectedMeta.mongoCollection
                        } : null,
                        relatedComponent: relatedComponent ? {
                            slug: relatedComponent.slug,
                            collection: relatedComponent.collection,
                            mongoCollection: relatedComponent.mongoCollection
                        } : null
                    });

                    setHasError(true);
                    setLoadErrorMessage(errorMsg);
                    lastFetchedRelateItemsRef.current = [];
                    setAllData([]);
                    return;
                }

                // Use slug for API calls - backend resolves slug to appropriate collection
                // Prefer slug from component metadata, fallback to table (which should be a slug)
                const slugToUse = relatedComponent?.slug || selectedMeta?.slug || table;
                console.log('🔗 RelationField: Using slug for API call', {
                    slugToUse,
                    relatedComponentSlug: relatedComponent?.slug,
                    selectedMetaSlug: selectedMeta?.slug,
                    table,
                    collectionName
                });
                const orgRaw = isOrganizationScopingDisabled()
                    ? (relatedComponent?.organization ?? '')
                    : listOrganization != null && String(listOrganization).trim() !== ''
                        ? String(listOrganization)
                        : (relatedComponent?.organization ?? selectedMeta?.organization ?? '');
                const orgNorm = normalizeOrgSegment(orgRaw);
                const result = await fetchItemsAPI(slugToUse, {
                    aclAction: 'relate',
                    limit: 2500,
                    ...(orgNorm && !isOrganizationScopingDisabled() ? { organization: orgNorm } : {}),
                });
                const items = result.items ?? [];
                lastFetchedRelateItemsRef.current = items.slice();

                console.log('RelationField: Fetched data', {
                    slugToUse,
                    itemsLength: items.length,
                    isAdmin,
                    firstItem: items[0] ? { uid: items[0].uid } : null
                });

                // Clear error state on successful fetch
                setHasError(false);
                setLoadErrorMessage("");

                console.log('RelationField: Setting allData', { count: items.length });
                setAllData(
                    mergeRelationPickerStubs(
                        lastFetchedRelateItemsRef.current,
                        currentForPickerRef.current,
                        relationFieldKey,
                        multiple
                    )
                );
                if (resolvedViaCatalogOnly) {
                    const s = String(slugToUse).replace(/[!]+$/, '').trim();
                    setSelectedMeta({ slug: s, fields: [] });
                }
            } catch (error) {
                console.error('Failed to fetch relation field data via API:', error);

                setHasError(true);
                setLoadErrorMessage(formatRelationFieldFetchError(error));
                lastFetchedRelateItemsRef.current = [];
                setAllData([]);
            } finally {
                isFetchingRef.current = false;
                setIsLoading(false);
                const slugRef = relatedComponent?.slug
                    ? String(relatedComponent.slug).replace(/[!]+$/, '').trim()
                    : typeof table === 'string'
                        ? table.replace(/[!]+$/, '').trim()
                        : '';
                lastFetchedMetaRef.current = relatedComponent?.uid
                    ? { uid: relatedComponent.uid, slug: slugRef || undefined }
                    : slugRef
                        ? { slug: slugRef }
                        : null;
            }
        };

        if (table && !isFetchingRef.current) {
            fetchData();
        }
    }, [table, isAdmin, selectedMeta, systemSource, listOrganization]);

    useEffect(() => {
        // Always sync currentState with latest Redux state
        console.log('RelationField: Syncing currentState from Redux current', {
            relationFieldKey,
            hasCurrent: !!current,
            currentKeys: current ? Object.keys(current) : [],
            currentValue: current?.[relationFieldKey],
            currentValueType: typeof current?.[relationFieldKey],
            isArray: Array.isArray(current?.[relationFieldKey])
        });
        setCurrentState(current || {});
    }, [current, relationFieldKey])

    // Debug: Log when allData changes
    useEffect(() => {
        console.log('RelationField: allData updated', {
            count: allData.length,
            items: allData.slice(0, 3).map((item: any) => ({
                uid: item.uid,
                name: item.name,
                first_name: item.first_name,
                last_name: item.last_name
            }))
        });
    }, [allData])

    const nestedRelationValue = getNestedField(current, relationFieldKey);
    const relationLabelSingular = current?.[`${relationFieldKey}__label`];
    const relationLabelsPlural = current?.[`${relationFieldKey}__labels`];
    const relationPickSignature = useMemo(() => {
        if (multiple) {
            const arr = Array.isArray(nestedRelationValue)
                ? nestedRelationValue
                : nestedRelationValue != null && nestedRelationValue !== ""
                    ? [nestedRelationValue]
                    : [];
            return JSON.stringify({
                m: 1,
                u: arr.map((x) => relationValueToUidString(x)),
                labels: relationLabelsPlural,
            });
        }
        return JSON.stringify({
            m: 0,
            u: relationValueToUidString(nestedRelationValue),
            label: relationLabelSingular,
        });
    }, [multiple, nestedRelationValue, relationLabelSingular, relationLabelsPlural, relationFieldKey])

    useEffect(() => {
        if (!current || typeof current !== "object") return;
        if (systemSource) {
            setAllData(
                mergeRelationPickerStubs(
                    lastSystemRelationItemsRef.current,
                    currentForPickerRef.current,
                    relationFieldKey,
                    multiple
                )
            );
            return;
        }
        if (!table) return;
        setAllData(
            mergeRelationPickerStubs(
                lastFetchedRelateItemsRef.current,
                currentForPickerRef.current,
                relationFieldKey,
                multiple
            )
        );
    }, [relationPickSignature, table, systemSource, relationFieldKey, multiple]);

    useEffect(() => {
        if (!currentState || typeof currentState !== 'object') {
            setSelectedUids([]);
            return;
        }

        if (!multiple) {
            // For single selection, handle nested paths
            if (relationFieldKey.includes('.')) {
                let keys = relationFieldKey.split('.');
                let value = currentState;
                for (let i = 0; i < keys.length; i++) {
                    if (value && typeof value === 'object' && keys[i] in value) {
                        value = value[keys[i]];
                    } else {
                        value = undefined;
                        break;
                    }
                }
                const u = relationValueToUidString(value);
                setSelectedUids(u ? [u] : []);
            } else {
                const u = relationValueToUidString(currentState[relationFieldKey]);
                setSelectedUids(u ? [u] : []);
            }
        } else {
            // For multiple selection, handle nested paths
            let _current;
            if (relationFieldKey.includes('.')) {
                let keys = relationFieldKey.split('.');
                let value = currentState;
                // Safely navigate the nested path
                for (let i = 0; i < keys.length; i++) {
                    if (value && typeof value === 'object' && keys[i] in value) {
                        value = value[keys[i]];
                    } else {
                        value = undefined;
                        break;
                    }
                }
                _current = Array.isArray(value) ? value : [];
            } else {
                const v = currentState[relationFieldKey];
                _current = Array.isArray(v) ? v : v != null && v !== "" ? [v] : [];
            }
            console.log('RelationField: Setting selectedUids from currentState', {
                relationFieldKey,
                currentStateValue: currentState[relationFieldKey],
                currentStateValueType: typeof currentState[relationFieldKey],
                currentStateKeys: Object.keys(currentState),
                _current,
                _currentType: Array.isArray(_current) ? 'array' : typeof _current,
                _currentLength: Array.isArray(_current) ? _current.length : 'N/A',
                multiple,
                allDataLength: allData.length,
                currentFromRedux: current?.[relationFieldKey],
                currentFromReduxType: typeof current?.[relationFieldKey]
            });
            const normalized = (_current || [])
                .map((x: any) => relationValueToUidString(x))
                .filter((id: string) => Boolean(id));
            setSelectedUids(normalized);
        }
    }, [currentState, relationFieldKey, multiple, allData.length, current]);

    useEffect(() => {
        const ids: any = readOnlyUids
        if (typeof (ids) === 'undefined') {
            return
        }

        // Add null check to prevent filter errors
        if (!allData || !Array.isArray(allData)) {
            setFilteredStatic([]);
            return;
        }

        const _filteredStatic = allData.filter((data: any) => ids?.includes(data.uid) || ids === data.uid);
        setFilteredStatic(_filteredStatic)
    }, [readOnlyUids, allData, fields]);

    useEffect(() => {
        if (typeof selectedMeta === 'undefined') {
            setDropdownFields([]);
            return;
        }

        // Parse fields - handle both comma and comma-space separators
        const fieldKeys = fields
            ? fields.split(',').map((f: string) => f.trim()).filter(Boolean)
            : [];
        const normalizedFieldKeys = fieldKeys.map((key: string) => key.toLowerCase());

        console.log('RelationField: Parsing fields', {
            fields,
            fieldKeys,
            selectedMetaFields: selectedMeta.fields?.map((f: any) => f.name),
            selectedMetaFieldsCount: selectedMeta.fields?.length
        });

        // If no fields specified, use first text field or name field as fallback
        if (fieldKeys.length === 0 && selectedMeta.fields && selectedMeta.fields.length > 0) {
            const fallbackField = selectedMeta.fields.find((f: any) =>
                f.type === 'text' || f.name === 'name' || f.name === 'label'
            ) || selectedMeta.fields[0];

            console.log('RelationField: No fields specified, using fallback:', fallbackField);
            setDropdownFields([fallbackField]);
            return;
        }

        const _fields = selectedMeta.fields?.filter((field: any) => {
            const fieldName = String(field.name || '').toLowerCase();
            const fieldLabel = String(field.label || '').toLowerCase();
            return normalizedFieldKeys.includes(fieldName) || normalizedFieldKeys.includes(fieldLabel);
        }) || [];

        console.log('RelationField: Filtered dropdown fields', {
            count: _fields.length,
            fieldNames: _fields.map((f: any) => f.name),
            allFieldNames: selectedMeta.fields?.map((f: any) => f.name)
        });

        setDropdownFields(_fields);
    }, [fields, selectedMeta])

    // Helper function to safely get nested property values
    const getNestedValue = (obj: any, path: string): any => {
        if (!obj || typeof obj !== 'object') return undefined;
        if (!path.includes('.')) {
            return obj[path];
        }
        const keys = path.split('.');
        let value = obj;
        for (let i = 0; i < keys.length; i++) {
            if (value && typeof value === 'object' && keys[i] in value) {
                value = value[keys[i]];
            } else {
                return undefined;
            }
        }
        return value;
    }

    // Build field config for debug
    const fieldConfig = {
        name: relationFieldKey,
        label: label,
        type: systemSource
            ? 'systemrelation'
            : (!multiple ? 'relation' : 'multiplerelation'),
        table: table,
        fields: fields,
        multiple: typeof (multiple) !== 'undefined' ? multiple : true,
        allowedGroupCount: Array.isArray(allowedGroups) ? allowedGroups.length : 0,
    }

    if (hasError) {
        const detail =
            loadErrorMessage.trim() ||
            "Could not load related records. Check permissions (view or field-scoped field_read on the related collection) and network.";

        const errorDebugProps = {
            fieldConfig,
            fieldValue: null,
            relatedData: { loadError: detail },
            label: label
        };

        const errorContent = (
            <div className="w-full rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/50 dark:text-red-100">
                <div className="flex items-start gap-2">
                    <svg className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="min-w-0 flex-1 space-y-1">
                        <div className="font-medium">Failed to load options</div>
                        <div className="text-xs break-words whitespace-pre-wrap opacity-95">{detail}</div>
                    </div>
                </div>
            </div>
        );

        if (hideLabel) {
            return (
                <div className="flex items-start gap-2 w-full">
                    {errorContent}
                    <DebugFieldButton {...errorDebugProps} />
                </div>
            );
        }

        return (
            <LabelTyped
                text={label}
                debugProps={errorDebugProps}
            >
                {errorContent}
            </LabelTyped>
        );
    }

    if (isLoading) {
        const debugProps = {
            fieldConfig,
            fieldValue: null,
            relatedData: null,
            label: label
        };

        const content = (
            <div className="w-full">
                <LinearProgress />
            </div>
        );

        if (hideLabel) {
            return (
                <div className="flex items-center gap-2 w-full">
                    {content}
                    <DebugFieldButton {...debugProps} />
                </div>
            );
        }

        return (
            <LabelTyped
                text={label}
                debugProps={debugProps}
            >
                {content}
            </LabelTyped>
        );
    }

    if (typeof (canEdit) !== 'undefined' && !canEdit) {
        const debugProps = {
            fieldConfig,
            fieldValue: filteredStatic.length > 0 ? filteredStatic.map((item: any) => item.uid) : null,
            relatedData: filteredStatic.length > 0 ? filteredStatic : null,
            label: label
        };

        if (filteredStatic.length > 0) {
            const content = (
                <div>
                    {filteredStatic.map((data: any, index: number) => {
                        return <div key={index} className="flex items-center gap-2">
                            {dropdownFields.map((field: any, fieldIndex: number) => {
                                return <span key={fieldIndex} style={{ fontSize: '.8rem' }}>
                                    {data[field.name]}
                                </span>
                            })}
                        </div>
                    })}
                </div>
            );

            if (hideLabel) {
                return (
                    <div className="flex items-center gap-2">
                        {content}
                        <DebugFieldButton {...debugProps} />
                    </div>
                );
            }

            return (
                <LabelTyped
                    text={label}
                    debugProps={debugProps}
                >
                    {content}
                </LabelTyped>
            )
        } else {
            const content = (
                <div>
                    no data found
                </div>
            );

            if (hideLabel) {
                return (
                    <div className="flex items-center gap-2">
                        {content}
                        <DebugFieldButton {...debugProps} />
                    </div>
                );
            }

            return (
                <LabelTyped
                    text={label}
                    debugProps={debugProps}
                >
                    {content}
                </LabelTyped>
            )
        }

    }


    const sortedRelationData = sortRelationRows(allData, dropdownFields);
    const relationTableCols = relationTableColumns(dropdownFields);
    const relationSelectorItems = sortedRelationData
        .filter((data: any) => data?.uid != null)
        .map((data: any) => relationRowToSelectorItem(data, dropdownFields));

    const relationEmptyPlaceholder = selectedMeta
        ? hasError
            ? "Error loading items"
            : relationSelectorItems.length
                ? "Not selected"
                : "No items available"
        : "Loading…";

    if (!multiple) {
        const rawValue =
            getNestedValue(current, relationFieldKey) ??
            getNestedValue(currentState, relationFieldKey);
        const currentValue = relationValueToUidString(rawValue);
        const relatedItem = currentValue
            ? allData.find((item: any) => String(item.uid) === currentValue)
            : undefined;
        const menuRows = buildSingleSelectMenuRows(
            sortedRelationData,
            dropdownFields,
            currentValue
        );
        const singleSelectorItems = [
            { value: "", label: "Not selected" },
            ...menuRows
                .filter((data: any) => data?.uid != null)
                .map((data: any) => relationRowToSelectorItem(data, dropdownFields)),
        ];

        return (
            <LabelTyped
                text={label}
                debugProps={{
                    fieldConfig,
                    fieldValue: rawValue,
                    relatedData: relatedItem,
                    label: label,
                }}
            >
                <ItemSelectorField
                    title={`Select ${label}`}
                    items={singleSelectorItems}
                    tableColumns={relationTableColumns(dropdownFields)}
                    detailCollectionFields={selectedMeta?.fields}
                    viewModeStorageKey={`relation-${relationFieldKey}`}
                    gridColumns={3}
                    value={currentValue}
                    disabled={!canEdit}
                    placeholder={relationEmptyPlaceholder}
                    emptyMessage="No items match your search."
                    onChange={(value) => {
                        const v =
                            typeof value === "string"
                                ? value
                                : Array.isArray(value)
                                    ? value[0] ?? ""
                                    : "";
                        onChange({ key: relationFieldKey, value: v });
                    }}
                />
            </LabelTyped>
        );
    }

    const selectedItems =
        selectedUids.length > 0
            ? allData.filter((item: any) =>
                selectedUids.some((uid: any) => String(uid) === String(item.uid))
            )
            : undefined;
    const multipleValue = selectedUids.map((uid: any) => String(uid));

    return (
        <LabelTyped
            text={label}
            debugProps={{
                fieldConfig,
                fieldValue: selectedUids,
                relatedData: selectedItems,
                label: label,
            }}
        >
            <ItemSelectorField
                multiple
                title={`Select ${label}`}
                description="Choose one or more related items."
                items={relationSelectorItems}
                tableColumns={relationTableColumns(dropdownFields)}
                detailCollectionFields={selectedMeta?.fields}
                viewModeStorageKey={`relation-${relationFieldKey}`}
                gridColumns={3}
                value={multipleValue}
                disabled={!canEdit}
                placeholder={
                    multipleValue.length > 0
                        ? `${multipleValue.length} selected`
                        : relationEmptyPlaceholder
                }
                emptyMessage="No items match your search."
                onChange={(values) => {
                    const arr = Array.isArray(values) ? values.map(String) : [];
                    setSelectedUids(arr);
                    onChange({ key: relationFieldKey, value: arr });
                }}
            />
        </LabelTyped>
    );
}

export default RelationField;